import {CloseButton, Skeleton, Typography, cn} from "@heroui/react";
import {CheckIcon, CodeIcon, CopyIcon, DownloadIcon} from "@project/icons-web";
import {AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion} from "motion/react";
import {Suspense, useEffect, useRef, useState} from "react";

import {generateBuilderCSS} from "../../themes/builder-utils";

import {HighlightedCode} from "./highlighted-code";
import {useThemeBuilder} from "./theme-builder-context";

/* -------------------------------------------------------------------------------------------------
 * Animation constants — mirroring the reference CodePanel
 * -----------------------------------------------------------------------------------------------*/

const PANEL_ANIMATION = {
  animate: {opacity: 1, x: 0},
  exit: {opacity: 0, x: "100%"},
  initial: {opacity: 0, x: "100%"},
  transition: {bounce: 0, duration: 0.4, type: "spring"} as const,
};

const BACKDROP_ANIMATION = {
  animate: {opacity: 1},
  exit: {opacity: 0},
  initial: {opacity: 0},
  transition: {duration: 0.3, ease: "easeOut"} as const,
};

/* -------------------------------------------------------------------------------------------------
 * Loading skeleton
 * -----------------------------------------------------------------------------------------------*/

function CodeLoadingSkeleton() {
  return (
    <div className="space-y-2.5 px-4 py-4">
      {[3 / 4, 1 / 2, 5 / 6, 2 / 3, 4 / 5, 1 / 3, 3 / 5, 2 / 5, 1 / 5, 4 / 5].map((w, i) => (
        <Skeleton key={i} className="h-4 rounded-md" style={{width: `${w * 100}%`}} />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------------------------------
 * ThemeCodePanel
 * -----------------------------------------------------------------------------------------------*/

/**
 * Animated code panel that slides in from the right showing the generated CSS.
 * Positioned to avoid overlapping the ThemeBuilderPanel bottom bar on desktop.
 * On mobile it expands to fill most of the viewport (after the drawer is closed).
 */
export function ThemeCodePanel() {
  const {isCodePanelOpen, params, semanticOverrides, toggleCodePanel} = useThemeBuilder();
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldReduceMotion = useReducedMotion();

  const currentPanelAnimation = shouldReduceMotion
    ? {
        animate: {opacity: 1, x: 0},
        exit: {opacity: 0, x: 0},
        initial: {opacity: 0, x: 0},
        transition: {duration: 0.2},
      }
    : PANEL_ANIMATION;

  const currentBackdropAnimation = shouldReduceMotion
    ? {
        animate: {opacity: 1},
        exit: {opacity: 0},
        initial: {opacity: 0},
        transition: {duration: 0.2},
      }
    : BACKDROP_ANIMATION;

  const cssCode = generateBuilderCSS(params, semanticOverrides);

  const handleCopy = () => {
    void navigator.clipboard.writeText(cssCode);
    setCopied(true);

    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([cssCode], {type: "text/plain"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "theme.css";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Cleanup copy timer on unmount
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (!isCodePanelOpen) return null;

  return (
    <LazyMotion strict features={domAnimation}>
      <AnimatePresence>
        {/* Backdrop */}
        <m.div
          key="theme-code-backdrop"
          aria-label="Close CSS panel"
          className="fixed inset-0 z-999 bg-black/40 backdrop-blur-sm"
          role="button"
          tabIndex={0}
          onClick={toggleCodePanel}
          onKeyDown={(e) => e.key === "Escape" && toggleCodePanel()}
          {...currentBackdropAnimation}
        />

        {/* Panel */}
        <m.aside
          key="theme-code-panel"
          aria-label="Generated CSS"
          className={cn(
            "fixed z-1000 flex flex-col overflow-hidden",
            "rounded-xl shadow-xl",
            "bg-background/95 border-border/40 border backdrop-blur-md",
            // mobile: fill viewport with margin
            "inset-4",
            // desktop: right side, above the builder panel (≈210px tall)
            "md:inset-auto md:top-4 md:right-6 md:bottom-52.5 md:w-[45%]",
          )}
          {...currentPanelAnimation}
        >
          {/* Header */}
          <header className="border-border/50 flex shrink-0 items-center justify-between border-b px-6 py-3">
            <div className="flex items-center gap-2">
              <CodeIcon className="text-foreground/50 size-4" aria-hidden />
              <Typography type="h3" className="text-foreground/70 text-sm font-medium">
                Generated CSS
              </Typography>
            </div>

            <div className="flex items-center gap-1">
              {/* Download */}
              <button
                aria-label="Download CSS"
                className="text-foreground/40 hover:bg-foreground/5 hover:text-foreground/70 flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors"
                type="button"
                onClick={handleDownload}
              >
                <DownloadIcon className="size-4" aria-hidden />
              </button>

              {/* Copy */}
              <button
                aria-label={copied ? "Copied!" : "Copy CSS"}
                data-copied={copied || undefined}
                className="text-foreground/40 hover:bg-foreground/5 hover:text-foreground/70 flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors data-copied:text-green-500"
                type="button"
                onClick={handleCopy}
              >
                {copied ? (
                  <CheckIcon className="size-4" aria-hidden />
                ) : (
                  <CopyIcon className="size-4" aria-hidden />
                )}
              </button>

              {/* Close */}
              <CloseButton aria-label="Close CSS panel" onPress={toggleCodePanel} />
            </div>
          </header>

          {/* Code content */}
          <div className="flex-1 overflow-auto">
            <Suspense fallback={<CodeLoadingSkeleton />}>
              <HighlightedCode code={cssCode} lang="css" />
            </Suspense>
          </div>
        </m.aside>
      </AnimatePresence>
    </LazyMotion>
  );
}
